import { Formik } from 'formik';
import React, { useEffect } from 'react';
import { Form } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import * as yup from 'yup';

import { PrimaryButton } from '@components/Button/variants';
import TextField from '@components/Form/TextField';
import TIModal from '@components/TIModal';
import { useProfileUpdate } from '@hooks';
import { CURRENT_USER_KEY } from '@utils/backend-api/currentUser';

import { nameSchema } from '../SettingsPage/Account/schema';

interface PromptForNameModalProps {
  userId: number;
  isOpen: boolean;
  onClose: () => void;
}

const PromptForNameModal = ({ userId, isOpen, onClose }: PromptForNameModalProps) => {
  const queryClient = useQueryClient();
  const { saveProfile, isSavingProfile, showSave, saveStatus, error } = useProfileUpdate({
    userId,
  });

  useEffect(() => {
    if (saveStatus === 'Saved') onClose();
    if (error) toast.error('There was an error updating your name, please try again.');
  }, [onClose, saveStatus, error]);

  const handleSubmit = async (values: { name: string }) => {
    saveProfile({ name: values.name, profile_enabled: true });
    // Mark current user as stale since we changed the name
    queryClient.invalidateQueries(CURRENT_USER_KEY);
  };

  const buttonText = isSavingProfile ? 'Saving' : showSave ? 'Saved' : 'Enable Profile';

  return (
    <TIModal
      isOpen={isOpen}
      onClose={onClose}
      id="PromptForNameModal"
      parentClass="w-[600px] max-w-full"
    >
      <div className="p-16 sm:p-32">
        <div className="mb-16 text-center font-suisse-works text-subheadline-sm">
          Your full name is required before you can enable your profile.
        </div>
        <Formik
          initialValues={{ name: '' }}
          onSubmit={handleSubmit}
          validationSchema={yup.object().shape({
            name: nameSchema,
          })}
        >
          {(formik) => (
            <Form className="my-24" noValidate onSubmit={formik.handleSubmit}>
              <TextField className="mb-24" name="name" label="Full Name" />
              <PrimaryButton
                as="button"
                type="submit"
                disabled={!(formik.isValid && formik.dirty) || isSavingProfile}
                fullWidth
              >
                {buttonText}
              </PrimaryButton>
            </Form>
          )}
        </Formik>
      </div>
    </TIModal>
  );
};

export default PromptForNameModal;
